import React, { useState, useEffect, useCallback } from "react";
import {
  addAIModelSetup,
  updateAIModelSetup,
  getAIModelSetupById,
} from "../../services/aiModelSetupsService";
import { getAIModelMastersList } from "../../services/aiModelMastersService";
import { validateAIModelSetup } from "../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../utils/errorHandler";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";

const INITIAL_FORM_STATE = {
  modelId: "",
  identityName: "",
  clientId: "",
  apiKey: "",
  isDefault: false,
};

const INITIAL_ERRORS_STATE = {
  modelId: "",
  identityName: "",
  clientId: "",
  apiKey: "",
};

const mapSetupResponse = (data) => ({
  modelId:
    data.modelId !== undefined && data.modelId !== null
      ? String(data.modelId)
      : data.ModelId !== undefined && data.ModelId !== null
      ? String(data.ModelId)
      : "",
  identityName: data.identityName || data.IdentityName || "",
  clientId: data.clientId || data.ClientId || "",
  apiKey: data.apiKey || data.ApiKey || "",
  isDefault: Boolean(data.isDefault ?? data.IsDefault ?? false),
});

export const AddAIModelSetup = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedSetup,
  setEditMode,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await getAIModelMastersList();
        const list = response.data?.result || response.data || [];
        setModelOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const setupId = initialData?.id ?? initialData?.Id;
      if (editMode && setupId) {
        try {
          const data = await getAIModelSetupById(setupId);
          setFormData(mapSetupResponse(data));
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleIsDefaultChange = ({ target: { checked } }) => {
    setFormData((prevData) => ({ ...prevData, isDefault: checked }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    setIsApiKeyVisible(false);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateAIModelSetup(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const setupId = initialData?.id ?? initialData?.Id;
      const payload = {
        modelId: Number(formData.modelId),
        identityName: formData.identityName.trim(),
        clientId: formData.clientId?.trim() || "",
        apiKey: formData.apiKey.trim(),
        isDefault: Boolean(formData.isDefault),
      };

      if (editMode) {
        payload.id = setupId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateAIModelSetup : addAIModelSetup;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `AI model setup ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        if (onSuccess) {
          onSuccess();
        }
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    resetForm();
    setSelectedSetup(null);
    setEditMode(false);
  };

  const getModelLabel = (item) => {
    const provider =
      item.providerType || item.ProviderType || "Unknown Provider";
    const modelName = item.modelName || item.ModelName || `Model ${item.id ?? item.Id}`;
    return `${provider} - (${modelName})`;
  };

  return (
    <>
      <ComponentHeader title="AI Model Setups" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update AI Model Setup" : "Add AI Model Setup"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="modelId" className="form-label">
                        AI Provider <span className="required-field">*</span>
                      </label>
                      <select
                        name="modelId"
                        id="modelId"
                        value={formData.modelId}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.modelId ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select AI Model</option>
                        {modelOptions.map((model) => {
                          const id = model.id ?? model.Id;
                          return (
                            <option key={id} value={String(id)}>
                              {getModelLabel(model)}
                            </option>
                          );
                        })}
                      </select>
                      {errors.modelId && (
                        <div className="invalid-feedback">{errors.modelId}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="identityName" className="form-label">
                        Identity Name{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="identityName"
                        id="identityName"
                        placeholder="Enter Identity Name"
                        value={formData.identityName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.identityName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.identityName && (
                        <div className="invalid-feedback">
                          {errors.identityName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="clientId" className="form-label">
                        Client ID
                      </label>
                      <input
                        type="text"
                        name="clientId"
                        id="clientId"
                        placeholder="Enter Client ID"
                        value={formData.clientId}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.clientId ? "is-invalid" : ""
                        }`}
                      />
                      {errors.clientId && (
                        <div className="invalid-feedback">{errors.clientId}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="apiKey" className="form-label">
                        API Key <span className="required-field">*</span>
                      </label>
                      <div className="position-relative auth-pass-inputgroup">
                        <input
                          type={isApiKeyVisible ? "text" : "password"}
                          name="apiKey"
                          id="apiKey"
                          placeholder="Enter API Key"
                          value={formData.apiKey}
                          onChange={handleInputChange}
                          className={`form-control pe-5 ${
                            errors.apiKey ? "is-invalid" : ""
                          }`}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted shadow-none password-addon mt-1"
                          onClick={() => setIsApiKeyVisible((prev) => !prev)}
                        >
                          <i
                            className={
                              isApiKeyVisible
                                ? "mdi mdi-eye-off align-middle"
                                : "mdi mdi-eye align-middle"
                            }
                          ></i>
                        </button>
                      </div>
                      {errors.apiKey && (
                        <div className="invalid-feedback d-block">
                          {errors.apiKey}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label d-block">Is Default</label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="setupIsDefault"
                          name="isDefault"
                          checked={formData.isDefault}
                          onChange={handleIsDefaultChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="setupIsDefault"
                        >
                          Set as default setup
                        </label>
                      </div>
                      {formData.isDefault && (
                        <small className="text-warning d-block mt-2">
                          Setting this setup as default may override existing
                          default setup.
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
